/**
 * TrackCellBadge Component
 * 
 * Displays the Track Cell for an item, which is the intersection of:
 * - Item Category (the type/classification)
 * - Altitude (decision significance level 1-5)
 * 
 * Track Cell = Item Category × Altitude
 * This determines which Track the item belongs to for prioritization.
 * 
 * Per v2.2 terminology: Items can only be compared within the same Track Cell.
 * 
 * Reference: 02 — Terminology and Glossary (v2.2)
 */

import { Grid2X2, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { UI_LABELS } from '../../lib/terminology';
import { useAltitudeText } from '../../hooks/prioritization/useAltitudeText';
import { CATEGORY_BADGE } from '../../lib/itemCategoryColors';

// Canonical six categories: colors from the shared token file.
// Legacy backlog types (feature, bug, etc.) keep local color strings.
const ITEM_CATEGORY_CONFIG: Record<string, { label: string; color: string }> = {
  problem:     { label: 'Problem',     color: `${CATEGORY_BADGE.problem.text} ${CATEGORY_BADGE.problem.bg}` },
  opportunity: { label: 'Opportunity', color: `${CATEGORY_BADGE.opportunity.text} ${CATEGORY_BADGE.opportunity.bg}` },
  risk:        { label: 'Risk',        color: `${CATEGORY_BADGE.risk.text} ${CATEGORY_BADGE.risk.bg}` },
  solution:    { label: 'Solution',    color: `${CATEGORY_BADGE.solution.text} ${CATEGORY_BADGE.solution.bg}` },
  commitment:  { label: 'Commitment',  color: `${CATEGORY_BADGE.commitment.text} ${CATEGORY_BADGE.commitment.bg}` },
  investment:  { label: 'Investment',  color: `${CATEGORY_BADGE.investment.text} ${CATEGORY_BADGE.investment.bg}` },
  // Legacy backlog types — not in the canonical palette, kept as local overrides.
  feature:     { label: 'Feature',     color: 'text-[var(--text-sec)] bg-[var(--slate-dim)]' },
  bug:         { label: 'Bug',         color: 'text-[var(--coral)] bg-[var(--coral-dim)]' },
  enhancement: { label: 'Enhancement', color: 'text-[var(--text-sec)] bg-[var(--slate-dim)]' },
  initiative:  { label: 'Initiative',  color: 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)]' },
  epic:        { label: 'Epic',        color: 'text-[var(--text-sec)] bg-[var(--slate-dim)]' },
  story:       { label: 'Story',       color: 'text-cyan-700 bg-cyan-100' },
  task:        { label: 'Task',        color: 'text-[var(--text-sec)] bg-[var(--hover-tint)]' },
};

function toTitleCase(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** Normalize category: e.g. "LFeature" (legacy/typo) -> "feature" for config lookup */
// eslint-disable-next-line react-refresh/only-export-components
export function normalizeItemCategory(raw: string | undefined): string | undefined {
  if (!raw || typeof raw !== 'string') return undefined;
  const s = raw.trim();
  if (!s) return undefined;
  const lower = s.toLowerCase();
  if (ITEM_CATEGORY_CONFIG[lower]) return lower;
  const withoutL = /^l([a-z]+)$/i.exec(s);
  if (withoutL && ITEM_CATEGORY_CONFIG[withoutL[1].toLowerCase()]) return withoutL[1].toLowerCase();
  return lower;
}

// eslint-disable-next-line react-refresh/only-export-components
export function getItemCategoryLabel(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const normalized = normalizeItemCategory(raw);
  if (!normalized) return undefined;
  return ITEM_CATEGORY_CONFIG[normalized]?.label ?? toTitleCase(raw.trim());
}

function parseAltitude(raw: number | string | undefined): number | undefined {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }

  if (typeof raw === 'string') {
    const parsed = Number.parseInt(raw, 10);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
}

// Altitude color configuration
const ALTITUDE_COLORS: Record<number, string> = {
  1: 'border-[var(--rule)] text-[var(--text-sec)]',
  2: 'border-[var(--slate)] text-[var(--text-ter)]',
  3: 'border-[var(--mustard)] text-[var(--mustard)]',
  4: 'border-[var(--mustard)] text-[var(--mustard)]',
  5: 'border-[var(--coral)] text-[var(--coral)]',
};

interface TrackCellBadgeProps {
  /** Item category (e.g., 'feature', 'bug', 'initiative') */
  itemCategory?: string;
  /** Altitude level (1-5) */
  altitude?: number;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Show formula label */
  showFormula?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** When true, wraps the badge in a link to the Track Cells reference page */
  linkToTrackCells?: boolean;
}

export function TrackCellBadge({
  itemCategory: rawCategory,
  altitude: rawAltitude,
  size = 'md',
  showFormula = false,
  className = '',
  linkToTrackCells = false,
}: TrackCellBadgeProps) {
  const altitudeText = useAltitudeText();
  const itemCategory = normalizeItemCategory(rawCategory);
  const altitude = parseAltitude(rawAltitude);
  const altitudeIsCanonical = altitude !== undefined && altitude >= 1 && altitude <= 5;
  if (!itemCategory && !altitude) {
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs text-[var(--text-ter)] bg-[var(--surface)] rounded border border-dashed border-[var(--rule)] ${className}`}>
        <Grid2X2 className="h-3 w-3" />
        No category or altitude
      </span>
    );
  }
  const categoryConfig = itemCategory
    ? ITEM_CATEGORY_CONFIG[itemCategory] || {
        label: getItemCategoryLabel(rawCategory || itemCategory) || 'Unclassified',
        color: 'text-[var(--text-sec)] bg-[var(--slate-dim)]',
      }
    : {
        label: 'Unclassified',
        color: 'text-[var(--text-sec)] bg-[var(--hover-tint)] border border-dashed border-[var(--rule)]',
      };
    
  const altitudeColor = altitude
    ? altitudeIsCanonical
      ? ALTITUDE_COLORS[altitude] || ALTITUDE_COLORS[1]
      : 'border-[var(--rule)] text-[var(--text-sec)] bg-[var(--surface)]'
    : '';
  const altitudeTitle = altitude
    ? altitudeIsCanonical
      ? altitudeText.format(altitude, itemCategory)
      : `Unrecognized altitude ${altitude}`
    : null;
  
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5 gap-1',
    md: 'text-sm px-2 py-1 gap-1.5',
    lg: 'text-base px-2.5 py-1.5 gap-2',
  };

  const trackCellsHref =
    linkToTrackCells && itemCategory && altitudeIsCanonical
      ? `/prioritization/leveling/track-cells?category=${itemCategory}&level=${altitude}`
      : null;

  const inner = (
    <div className={`inline-flex items-center ${sizeClasses[size]} ${trackCellsHref ? 'hover:opacity-80 transition-opacity cursor-pointer' : ''} ${className}`}>
      {showFormula && (
        <span className="text-[var(--text-ter)] font-medium mr-1">
          {UI_LABELS.trackCell}:
        </span>
      )}
      
      {/* Item Category part */}
      {categoryConfig && (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-l font-medium ${categoryConfig.color}`}>
          {categoryConfig.label}
        </span>
      )}
      
      {/* Connector */}
      {categoryConfig && altitude && (
        <span className="text-[var(--text-ter)] text-xs px-0.5">×</span>
      )}
      
      {/* Altitude part: the number first, the name as a tag ("Altitude 3 · Epic") */}
      {altitude && (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-r border-2 font-medium ${altitudeColor}`} title={altitudeTitle ?? undefined}>
          <Layers className={size === 'sm' ? 'h-3 w-3 mr-0.5' : 'h-3.5 w-3.5 mr-1'} />
          {altitudeIsCanonical ? altitudeText.format(altitude, itemCategory) : altitude}
        </span>
      )}
    </div>
  );

  if (trackCellsHref) {
    return (
      <Link to={trackCellsHref} title={`View grid cell: ${altitudeTitle}`}>
        {inner}
      </Link>
    );
  }

  return inner;
}

/**
 * Compact Track Cell indicator for item cards
 */
export function TrackCellIndicator({
  itemCategory: rawCategory,
  altitude: rawAltitude,
  className = '',
}: Pick<TrackCellBadgeProps, 'itemCategory' | 'altitude' | 'className'>) {
  const altitudeText = useAltitudeText();
  const itemCategory = normalizeItemCategory(rawCategory);
  const altitude = parseAltitude(rawAltitude);
  const altitudeIsCanonical = altitude !== undefined && altitude >= 1 && altitude <= 5;
  if (!itemCategory && !altitude) {
    return null;
  }
  const categoryConfig = itemCategory
    ? ITEM_CATEGORY_CONFIG[itemCategory] || {
        label: getItemCategoryLabel(rawCategory || itemCategory) || 'Unclassified',
        color: 'text-[var(--text-sec)] bg-[var(--slate-dim)]',
      }
    : {
        label: 'Unclassified',
        color: 'text-[var(--text-sec)] bg-[var(--hover-tint)] border border-dashed border-[var(--rule)]',
      };
    
  const altitudeColor = altitude
    ? altitudeIsCanonical
      ? ALTITUDE_COLORS[altitude] || ALTITUDE_COLORS[1]
      : 'border-[var(--rule)] text-[var(--text-sec)] bg-[var(--surface)]'
    : '';
  const altitudeTitle = altitude
    ? altitudeIsCanonical
      ? altitudeText.format(altitude, itemCategory)
      : `Unrecognized altitude ${altitude}`
    : null;
  
  return (
    <div 
      className={`inline-flex items-center gap-1 text-xs ${className}`}
      title={altitudeTitle ? `${categoryConfig.label} × ${altitudeTitle}` : categoryConfig.label}
    >
      <span className={`px-1.5 py-0.5 rounded ${categoryConfig.color}`}>
        {categoryConfig.label}
      </span>
      {altitude && (
        <span className={`px-1.5 py-0.5 rounded border ${altitudeColor}`} title={altitudeTitle ?? undefined}>
          {altitude}
        </span>
      )}
    </div>
  );
}

export default TrackCellBadge;
